import React, { useMemo, useState } from "react";

import "./trip-approval-strip.css";

const TripApprovalBanner = ({ trip, onDecision, disabled, canApprove }) => {
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [busyAction, setBusyAction] = useState(null);

  const qualifyingContract = useMemo(() => {
    return (trip?.contractData || []).find(
      (c) =>
        c?.stage === "signed" &&
        (c?.featuredOneWayId || c?.clickToFlyResultId)
    );
  }, [trip?.contractData]);

  if (!canApprove || !trip || trip.stage !== "inReview" || !qualifyingContract) {
    return null;
  }

  const source = qualifyingContract.clickToFlyResultId
    ? "ClickToFly"
    : "Featured One-Way";

  const busy = !!busyAction || disabled;

  const handleApprove = async () => {
    if (busy) return;
    setBusyAction("approve");
    try {
      await onDecision("approve");
    } finally {
      setBusyAction(null);
    }
  };

  const handleOpenDecline = () => {
    if (busy) return;
    setDeclineReason("");
    setDeclineOpen(true);
  };

  const handleCancelDecline = () => {
    if (busyAction) return;
    setDeclineOpen(false);
  };

  const handleConfirmDecline = async () => {
    if (busy) return;
    setBusyAction("decline");
    try {
      await onDecision("decline", declineReason.trim() || undefined);
      setDeclineOpen(false);
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <section
      className="trip-approval-strip"
      aria-label="Trip approval required"
    >
      <div className="trip-approval-strip__main">
        <div className="trip-approval-strip__content">
          <span className="trip-approval-strip__tag">
            <span className="trip-approval-strip__tag-dot" aria-hidden="true" />
            Pending Review
          </span>
          <h3 className="trip-approval-strip__headline">
            This trip is awaiting admin approval
          </h3>
          <p className="trip-approval-strip__subhead">
            A signed{" "}
            <span className="trip-approval-strip__source">{source}</span>{" "}
            contract needs to be reviewed before the trip goes active.
          </p>
        </div>

        <div className="trip-approval-strip__actions">
          <button
            type="button"
            className="trip-approval-strip__btn trip-approval-strip__btn--danger"
            onClick={handleOpenDecline}
            disabled={busy || declineOpen}
          >
            Decline
          </button>
          <button
            type="button"
            className="trip-approval-strip__btn trip-approval-strip__btn--primary"
            onClick={handleApprove}
            disabled={busy}
          >
            {busyAction === "approve" ? "Approving…" : "Approve"}
          </button>
        </div>
      </div>

      <div
        className={
          "trip-approval-strip__panel-wrapper" +
          (declineOpen ? " trip-approval-strip__panel-wrapper--open" : "")
        }
      >
        <div className="trip-approval-strip__panel-inner">
          <div className="trip-approval-strip__panel">
            <label
              htmlFor="trip-approval-strip-reason"
              className="trip-approval-strip__panel-label"
            >
              <span>Reason for decline</span>
              <span className="trip-approval-strip__panel-label-hint">
                Optional — visible in audit trail
              </span>
            </label>
            <textarea
              id="trip-approval-strip-reason"
              className="trip-approval-strip__reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Why is this trip being declined?"
              rows={3}
              disabled={busyAction === "decline"}
            />
            <div className="trip-approval-strip__panel-actions">
              <button
                type="button"
                className="trip-approval-strip__btn trip-approval-strip__btn--ghost"
                onClick={handleCancelDecline}
                disabled={busyAction === "decline"}
              >
                Cancel
              </button>
              <button
                type="button"
                className="trip-approval-strip__btn trip-approval-strip__btn--confirm-danger"
                onClick={handleConfirmDecline}
                disabled={busy}
              >
                {busyAction === "decline" ? "Declining…" : "Confirm Decline"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripApprovalBanner;
